import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { CompanyUsers, CompanyUsersFilters } from '../models/company-users.model';
import * as companyUsersService from '../services/company-users.service';

export const useCompanyUsers = (defaultFilters: CompanyUsersFilters & { searchParams?: { ids?: string[] } }) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<CompanyUsersFilters>>({});

  const mergedFilters = { ...otherDefaultFilters, ...filters };

  const updateFilters = useCallback((newFilters: Partial<CompanyUsersFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(
    companyUsersService.companyUsersQueryOptions(mergedFilters, { enabled: Boolean(mergedFilters.companyId) }),
  );

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length) {
      return [];
    }
    if (!data?.map) {
      return searchParams.ids;
    }
    return searchParams.ids.filter((id) => !data.map[id]);
  }, [searchParams?.ids, data?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    companyUsersService.companyUsersQueryOptions(
      { companyId: mergedFilters.companyId, search: { id: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );
  const mergedMap: CompanyUsers['map'] = { ...(data?.map ?? {}), ...(preloadData?.map ?? {}) };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
